/* ==========================================================================
   ep-wishlist.js - Everetts Place native wishlist
   No account, no backend, no app. State lives in localStorage; sharing is
   done by encoding the product handles into the /pages/wishlist URL.

   Public API (window.EPWishlist):
     .items()            -> [{h: handle, t: title, ts: added}]
     .handles()          -> [handle]
     .count()            -> number
     .has(handle)        -> bool
     .add(handle, title) / .remove(handle) / .toggle(handle, title)
     .addMany([{h,t}])   -> number added
     .clear()
   Fires document event 'ep-wishlist:change' with detail {count, handles}.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'ep_wishlist_v1';
  var MAX = 200;
  var PAGE = '/pages/wishlist';

  /* ---------------------------------------------------------------- store */
  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return [];
      var list = JSON.parse(raw);
      if (!Array.isArray(list)) return [];
      return list.filter(function (i) { return i && typeof i.h === 'string' && i.h; });
    } catch (e) { return []; }
  }

  function write(list) {
    try { window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) { /* private mode */ }
    emit();
  }

  function emit() {
    var detail = { count: count(), handles: handles() };
    document.dispatchEvent(new CustomEvent('ep-wishlist:change', { detail: detail }));
  }

  function items() { return read(); }
  function handles() { return read().map(function (i) { return i.h; }); }
  function count() { return read().length; }
  function has(h) { return read().some(function (i) { return i.h === h; }); }

  function add(h, t) {
    if (!h || has(h)) return false;
    var list = read();
    list.unshift({ h: h, t: t || '', ts: Date.now() });
    write(list);
    return true;
  }

  function remove(h) {
    var list = read();
    var next = list.filter(function (i) { return i.h !== h; });
    if (next.length === list.length) return false;
    write(next);
    return true;
  }

  function toggle(h, t) { return has(h) ? (remove(h), false) : (add(h, t), true); }

  function addMany(entries) {
    var list = read();
    var seen = {};
    list.forEach(function (i) { seen[i.h] = 1; });
    var added = 0;
    entries.forEach(function (e) {
      var h = typeof e === 'string' ? e : e && e.h;
      if (!h || seen[h]) return;
      seen[h] = 1;
      added++;
      list.unshift({ h: h, t: (e && e.t) || '', ts: Date.now() });
    });
    if (added) write(list);
    return added;
  }

  function clear() { write([]); }

  /* --------------------------------------------------------------- helpers */
  function handleFromUrl(url) {
    if (!url) return '';
    var m = String(url).split('#')[0].split('?')[0].match(/\/products\/([^\/]+)\/?$/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function money(cents) {
    var amount = (cents || 0) / 100;
    try {
      var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var toastEl = null, toastTimer = null;
  function toast(msg) {
    if (!toastEl) {
      toastEl = el('div', 'ep-wl-toast');
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-open');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-open'); }, 2600);
  }

  var HEART = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path class="ep-wl-heart-fill" fill="currentColor" d="M12 21s-7.5-4.7-9.6-9A5.4 5.4 0 0 1 12 6.3 5.4 5.4 0 0 1 21.6 12c-2.1 4.3-9.6 9-9.6 9z"/>' +
    '<path d="M12 20.3S4.9 15.9 3 12a5 5 0 0 1 9-3.9A5 5 0 0 1 21 12c-1.9 3.9-9 8.3-9 8.3z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
    '</svg>';

  /* ------------------------------------------------------- toggle buttons  */
  function paint(btn) {
    var on = has(btn.getAttribute('data-ep-wl-handle'));
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    var label = on ? 'Remove from wishlist' : 'Save to wishlist';
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
    var txt = btn.querySelector('.ep-wl-btn-txt');
    if (txt) txt.textContent = on ? 'Saved to wishlist' : 'Save to wishlist';
  }

  function makeButton(handle, title, variant) {
    var btn = el('button', 'ep-wl-btn ep-wl-btn--' + variant);
    btn.type = 'button';
    btn.setAttribute('data-ep-wl-handle', handle);
    if (title) btn.setAttribute('data-ep-wl-title', title);
    btn.innerHTML = HEART + (variant === 'pdp' ? '<span class="ep-wl-btn-txt">Save to wishlist</span>' : '');
    paint(btn);
    return btn;
  }

  function onToggleClick(e) {
    if (!e.target || !e.target.closest) return;
    var btn = e.target.closest('.ep-wl-btn');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var h = btn.getAttribute('data-ep-wl-handle');
    if (!h) return;
    var on = toggle(h, btn.getAttribute('data-ep-wl-title') || '');
    btn.classList.remove('is-popping');
    void btn.offsetWidth;
    if (on) btn.classList.add('is-popping');
    toast(on ? 'Saved to your wishlist' : 'Removed from your wishlist');
  }

  /* ------------------------------------------------------- card injection  */
  function injectCards(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var cards = scope.querySelectorAll('.card-product, [data-ep-wl-card]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (card.querySelector(':scope > .ep-wl-btn')) continue;
      var link = card.querySelector('a[href*="/products/"]');
      if (!link) continue;
      var handle = handleFromUrl(link.getAttribute('href'));
      if (!handle) continue;
      var titleEl = card.querySelector('.x-card-title, .card__heading a, .card__heading');
      var title = (titleEl ? titleEl.textContent : link.getAttribute('aria-label') || '').trim();
      if (getComputedStyle(card).position === 'static') card.style.position = 'relative';
      card.appendChild(makeButton(handle, title, 'card'));
    }
  }

  /* -------------------------------------------------------- PDP injection  */
  /*  The theme hydrates the buy form after DOMContentLoaded, so this runs
      again from the MutationObserver until a real form shows up. Skip the
      Shop Pay installments form, the sticky add-to-cart bar and any form
      that belongs to a product card.                                        */
  function findPdpForm() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var f = forms[i];
      if (/installment/i.test(f.className || '')) continue;
      if (f.closest && f.closest('.card-product, [data-ep-wl-card], .quick-view, #CartDrawer, .cart-drawer')) continue;
      if (f.querySelector('[name="add"], button[type="submit"]')) return f;
    }
    return null;
  }

  function injectPdp() {
    var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!m) return;
    if (document.querySelector('.ep-wl-btn--pdp')) return;
    var handle = decodeURIComponent(m[1]);
    var form = findPdpForm();
    if (!form) return;
    var h1 = document.querySelector('h1');
    var btn = makeButton(handle, h1 ? h1.textContent.trim() : '', 'pdp');
    // sit under the whole buy-buttons block when the theme groups them
    var anchor = (form.closest && form.closest('.x-block-buy-buttons')) || form;
    if (!anchor.parentNode) return;
    anchor.parentNode.insertBefore(btn, anchor.nextSibling);
  }

  /* --------------------------------------------------------- header badge  */
  function paintHeader() {
    var n = count();
    var badges = document.querySelectorAll('.ep-wl-count');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = n > 99 ? '99+' : String(n);
      badges[i].hidden = n === 0;
    }
    var links = document.querySelectorAll('.ep-wl-link');
    for (var j = 0; j < links.length; j++) {
      links[j].setAttribute('aria-label', n === 1 ? 'Wishlist, 1 item' : 'Wishlist, ' + n + ' items');
    }
  }

  function repaintAll() {
    paintHeader();
    var btns = document.querySelectorAll('.ep-wl-btn');
    for (var i = 0; i < btns.length; i++) paint(btns[i]);
  }

  /* ----------------------------------------------------------- share URLs  */
  function shareUrl(list) {
    var hs = (list || handles()).slice(0, 60);
    return window.location.origin + PAGE + '?list=' + encodeURIComponent(hs.join(','));
  }

  function sharedHandles() {
    var q = new URLSearchParams(window.location.search).get('list');
    if (!q) return null;
    var hs = q.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    return hs.length ? hs.slice(0, 60) : null;
  }

  /* -------------------------------------------------------- wishlist page  */
  function fetchProduct(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function skeletons(grid, n) {
    grid.innerHTML = '';
    for (var i = 0; i < n; i++) {
      var c = el('div', 'ep-wl-card');
      c.innerHTML = '<div class="ep-wl-skel ep-wl-skel-media"></div>' +
        '<div class="ep-wl-skel ep-wl-skel-line"></div>' +
        '<div class="ep-wl-skel ep-wl-skel-line short"></div>';
      grid.appendChild(c);
    }
  }

  function cardMarkup(p) {
    var url = '/products/' + p.handle;
    var img = p.featured_image
      ? String(p.featured_image).replace(/(\.[a-z]+)(\?|$)/i, '_600x$1$2')
      : null;
    var available = !!p.available;
    var variants = p.variants || [];
    var single = variants.length === 1;
    var priceHtml = p.compare_at_price_min > p.price_min
      ? '<s>' + money(p.compare_at_price_min) + '</s>' + money(p.price_min)
      : money(p.price_min);
    if (p.price_min !== p.price_max) priceHtml = 'From ' + money(p.price_min);

    var action;
    if (!available) {
      action = '<button class="ep-wl-add" type="button" disabled>Sold out</button>';
    } else if (single) {
      action = '<button class="ep-wl-add" type="button" data-ep-wl-add="' + variants[0].id + '">Add to bag</button>';
    } else {
      action = '<a class="ep-wl-add" href="' + url + '">Choose options</a>';
    }

    return '' +
      '<a class="ep-wl-media" href="' + url + '" aria-hidden="true" tabindex="-1">' +
        (img ? '<img src="' + esc(img) + '" alt="" loading="lazy" width="600" height="600">' : '') +
      '</a>' +
      (p.vendor ? '<div class="ep-wl-vendor">' + esc(p.vendor) + '</div>' : '<div class="ep-wl-vendor"></div>') +
      '<h2 class="ep-wl-title"><a href="' + url + '">' + esc(p.title) + '</a></h2>' +
      '<div class="ep-wl-price">' + (available ? priceHtml : '<span class="ep-wl-oos">Currently unavailable</span>') + '</div>' +
      '<div class="ep-wl-card-actions">' + action +
        '<button class="ep-wl-remove" type="button" data-ep-wl-remove="' + esc(p.handle) + '">Remove</button>' +
      '</div>';
  }

  function addToCart(variantId, btn) {
    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function () {
        btn.textContent = 'Added';
        toast('Added to your bag');
        return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          bubble.setAttribute('data-value', cart.item_count);
          var inner = bubble.querySelector('span');
          if (inner) inner.textContent = cart.item_count < 100 ? cart.item_count : '99+';
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        setTimeout(function () { btn.disabled = false; btn.textContent = original; }, 1800);
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = original;
        toast('Sorry, that could not be added to the bag.');
      });
  }

  /* ------------------------------------------------- registry intake link  */
  /*  The wishlist feeds the concierge registry: "Turn this into a registry"
      hands the hearted handles to /pages/create-a-registry?list=..., and the
      script below drops the list into the intake form's product textarea so
      it arrives in the request email the runbook already works from.        */
  function registryUrl(list) {
    var hs = (list || handles()).slice(0, 60);
    if (!hs.length) return '/pages/create-a-registry';
    return '/pages/create-a-registry?list=' + encodeURIComponent(hs.join(','));
  }

  function initRegistryIntake() {
    var box = document.getElementById('ep-cr-products');
    if (!box) return;

    var fromLink = sharedHandles();
    var hs = fromLink || handles();
    if (!hs.length) return;

    function listText(products) {
      return products.map(function (p) {
        return '- ' + p.title + ' (' + money(p.price_min) + ') ' +
          window.location.origin + '/products/' + p.handle;
      }).join('\n');
    }

    function fill(products, announce) {
      var text = 'From my Everetts Place wishlist:\n' + listText(products);
      box.value = box.value.trim() ? box.value.trim() + '\n\n' + text : text;
      box.dispatchEvent(new Event('input', { bubbles: true }));
      if (announce) toast(products.length + (products.length === 1 ? ' wishlist item added' : ' wishlist items added'));
    }

    Promise.all(hs.map(fetchProduct)).then(function (results) {
      var products = results.filter(Boolean);
      if (!products.length) return;

      if (fromLink) {
        fill(products, false);
        return;
      }
      // No ?list= in the URL, but this browser has a wishlist: offer it.
      var btn = el('button', 'ep-wl-act ep-wl-act--quiet');
      btn.type = 'button';
      btn.style.marginBottom = '10px';
      btn.innerHTML = HEART + '<span>Add my ' + products.length +
        (products.length === 1 ? ' wishlist item' : ' wishlist items') + '</span>';
      btn.addEventListener('click', function () { fill(products, true); btn.remove(); });
      box.parentNode.insertBefore(btn, box);
    });
  }

  function initPage() {
    var page = document.querySelector('[data-ep-wishlist-page]');
    if (!page) return;

    var grid = page.querySelector('.ep-wl-grid');
    var empty = page.querySelector('.ep-wl-empty');
    var bar = page.querySelector('.ep-wl-bar');
    var barCount = page.querySelector('.ep-wl-bar-count');
    var shared = page.querySelector('.ep-wl-shared');
    var sub = page.querySelector('.ep-wl-sub');
    var shareList = sharedHandles();
    var viewing = shareList || handles();
    var loaded = [];

    if (shareList) {
      shared.hidden = false;
      if (sub) sub.textContent = 'Someone shared these picks with you. Save the list to keep it on your own device.';
    }

    var band = page.querySelector('.ep-wl-band');
    var regCta = page.querySelector('[data-ep-wl-registry]');

    function render() {
      grid.innerHTML = '';
      if (!loaded.length) {
        bar.hidden = true;
        if (band) band.hidden = true;
        empty.hidden = false;
        return;
      }
      empty.hidden = true;
      bar.hidden = false;
      if (band) band.hidden = false;
      if (regCta) {
        regCta.setAttribute('href', registryUrl(loaded.map(function (p) { return p.handle; })));
      }
      barCount.textContent = loaded.length === 1 ? '1 item saved' : loaded.length + ' items saved';
      loaded.forEach(function (p) {
        var card = el('div', 'ep-wl-card');
        card.setAttribute('data-handle', p.handle);
        card.innerHTML = cardMarkup(p);
        if (shareList) {
          var rm = card.querySelector('.ep-wl-remove');
          if (rm) rm.remove();
        }
        grid.appendChild(card);
      });
    }

    if (!viewing.length) {
      grid.innerHTML = '';
      bar.hidden = true;
      empty.hidden = false;
    } else {
      skeletons(grid, Math.min(viewing.length, 8));
      Promise.all(viewing.map(fetchProduct)).then(function (results) {
        loaded = results.filter(Boolean);
        // Drop handles that no longer resolve (deleted/unpublished products).
        if (!shareList) {
          var live = {};
          loaded.forEach(function (p) { live[p.handle] = 1; });
          var stale = items().filter(function (i) { return !live[i.h]; });
          stale.forEach(function (i) { remove(i.h); });
        }
        render();
      });
    }

    // toolbar actions
    page.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var addBtn = e.target.closest('[data-ep-wl-add]');
      if (addBtn) { addToCart(addBtn.getAttribute('data-ep-wl-add'), addBtn); return; }

      var rmBtn = e.target.closest('[data-ep-wl-remove]');
      if (rmBtn) {
        var h = rmBtn.getAttribute('data-ep-wl-remove');
        remove(h);
        loaded = loaded.filter(function (p) { return p.handle !== h; });
        render();
        toast('Removed from your wishlist');
        return;
      }

      var saveAll = e.target.closest('[data-ep-wl-saveall]');
      if (saveAll) {
        var added = addMany(loaded.map(function (p) { return { h: p.handle, t: p.title }; }));
        toast(added ? added + (added === 1 ? ' item saved to your wishlist' : ' items saved to your wishlist')
                    : 'Those are already on your wishlist');
        return;
      }

      var shareBtn = e.target.closest('[data-ep-wl-share]');
      if (shareBtn) {
        var url = shareList ? window.location.href : shareUrl();
        var data = { title: 'My Everetts Place wishlist', text: 'Here are the baby picks on my wishlist:', url: url };
        if (navigator.share) {
          navigator.share(data).catch(function () { /* dismissed */ });
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function () { toast('Share link copied to your clipboard'); })
            .catch(function () { window.prompt('Copy your wishlist link:', url); });
        } else {
          window.prompt('Copy your wishlist link:', url);
        }
        return;
      }

      var mailBtn = e.target.closest('[data-ep-wl-email]');
      if (mailBtn) {
        var link = shareList ? window.location.href : shareUrl();
        var lines = loaded.slice(0, 20).map(function (p) {
          return '- ' + p.title + ' (' + money(p.price_min) + ')\n  ' + window.location.origin + '/products/' + p.handle;
        }).join('\n');
        var body = 'Here is my wishlist from Everetts Place:\n\n' + lines +
          (loaded.length > 20 ? '\n- ...and ' + (loaded.length - 20) + ' more\n' : '\n') +
          '\nView the full list: ' + link + '\n\nFree delivery on orders over $75.\n';
        mailBtn.setAttribute('href', 'mailto:?subject=' + encodeURIComponent('My Everetts Place wishlist') +
          '&body=' + encodeURIComponent(body));
      }
    });

    document.addEventListener('ep-wishlist:change', function () {
      if (shareList) return;
      var hs = handles();
      loaded = loaded.filter(function (p) { return hs.indexOf(p.handle) !== -1; });
      render();
    });
  }

  /* ------------------------------------------------------------------ boot */
  function scan() {
    injectCards(document);
    injectPdp();
    repaintAll();
  }

  function init() {
    document.addEventListener('click', onToggleClick, true);
    scan();
    initPage();
    initRegistryIntake();

    // setTimeout rather than requestAnimationFrame: rAF never fires in a
    // background/hidden tab, which would leave late-rendered grids heartless.
    var pending = false;
    var mo = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        injectCards(document);
        injectPdp();
        repaintAll();
      }, 60);
    });
    mo.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('ep-wishlist:change', repaintAll);
    window.addEventListener('storage', function (e) { if (e.key === KEY) repaintAll(); });
    window.addEventListener('pageshow', repaintAll);
  }

  window.EPWishlist = {
    items: items, handles: handles, count: count, has: has,
    add: add, remove: remove, toggle: toggle, addMany: addMany, clear: clear,
    shareUrl: shareUrl, registryUrl: registryUrl, refresh: scan
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
